import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import CustomerSelector from '../components/CustomerSelector';
import { useTheme, useMediaQuery } from '@mui/material';
import CartIcon from './icons/CartIcon';
import UserIcon from './icons/User';
import SearchIcon from './icons/Search';
import Search from './Search';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { NavHashLink as Link } from 'react-router-hash-link';
import Auth from '../services/Auth';
import CheckBox from '../components/form-controls/CheckBox';
import SaveFiltersToggle from './SaveFiltersToggle';
import Api from '../services/Api';
import UsersService from '../services/UserService';
import CustomerService from '../services/Customer';
import Notifications from '../services/Notifications';
import Authorize from './Authorize';
import * as userActionsFunctions from '../actions/userActions';
import * as Roles from '../resources/UserRole';
import CartService from '../services/CartService';
import * as cartActionsFunctions from '../actions/cartActions';
import { useHistory } from 'react-router-dom';
import { useCustomers } from '../hooks/CustomerContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { useStorefrontSettings, withStorefrontConfig } from '../hooks/StorefrontSettingsContext';
import LanguageSelect from './LanguageSelect';
import EditName from './users/EditName';
import MenuIcon from './icons/Menu';
import { useAllFilters } from '../hooks/useAllFilters';
import { useSaveFiltersFlag } from "../hooks/useSaveFiltersFlag";

export const UserBar = (props) => {
    const storefrontConfig = useStorefrontSettings();
    const history = props.history ?? useHistory();
    const { customerList, currentCustomer, setCurrentCustomer, setCustomerList } = useCustomers();
    const dispatch = props.cartActions ? {} : useDispatch();
    const cartActions = props.cartActions ? props.cartActions : bindActionCreators(cartActionsFunctions, dispatch);
    const userActions = props.userActions ? props.userActions : bindActionCreators(userActionsFunctions, dispatch);
    const setLocale = (locale) => {
        props.StorefrontConfig.updateLocale(locale);
        userActions.updateUserLocale(locale);
    };
    const localized = storefrontConfig.labels;
    const [searchIsActive, setSearchIsActive] = useState(false);
    const [userMenuIsActive, setUserMenuIsActive] = useState(false);
    const curNode = useRef([]);
    const nodeToggle = useRef([]);
    const store = props.store ? props.store : useSelector((state) => state);
    const currentUser = store.user.currentUser;
    const cartCount = store.cart.items.length;
    const storeCustomerList = store.user.customers;
    const [defaultFHC, setDefaultFHC] = useState('');
    const fetchedPrefsKeysRef = useRef(new Set());
    const { invoice, orders, quotes } = useAllFilters();
    const { isSaveFiltersEnabled } = useSaveFiltersFlag(userActions.SetUserFlags);

    useEffect(() => {
        document.addEventListener('mousedown', handleClick, false);
        setDefaultFHC(storefrontConfig.settingsList.DefaultFreightHandlingCode);
        return function cleanup() {
            document.removeEventListener('mousedown', handleClick, false);
        };
    }, [storefrontConfig.storefrontSettingsLoaded]);

    useEffect(() => {
        setCustomerList(storeCustomerList);
        if (!currentCustomer?.id && storeCustomerList.length > 0) {
            setCurrentCustomer(storeCustomerList[0]);
        } else if (currentCustomer && storeCustomerList.length < 1) {
            setCurrentCustomer({});
        }
    }, [storeCustomerList]);


    useEffect(() => {
        if (!storefrontConfig.storefrontSettingsLoaded) return;
        const c = currentCustomer;
        if (!c?.companyCode || !c?.externalId) return;
        const key = `${c.externalId}|${c.companyCode}`;
        if (fetchedPrefsKeysRef.current.has(key)) return;
        fetchedPrefsKeysRef.current.add(key);
        loadCustomerPreferences(c, null);
    }, [
        storefrontConfig.storefrontSettingsLoaded,
        currentCustomer?.externalId,
        currentCustomer?.companyCode,
    ]);

    const handleClick = (e) => {
        if (curNode.current.contains(e.target) || nodeToggle.current.contains(e.target)) return;
        setUserMenuIsActive(false);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true });

    const updateSelectedCustomer = (customer) => {
        if (!customer || (currentCustomer && currentCustomer.id === customer.id)) {
            return;
        }

        const clearCartAndSetCustomer = () => {
            Notifications.confirmation(localized.ConfirmClearCart, () => {
                customer = setCustomer(customer, (cust) => {
                    cartActions.ClearCart(
                        currentUser.id,
                        cust.paymentTermId,
                        cust.paymentInstrument,
                        storefrontConfig.settingsList.DefaultFreightHandlingCode ?? defaultFHC
                    );
                    saveCustomerSelection(customer);
                });
            });
        };

        const loadPreferencesAndShowCart = () => {
            loadCustomerPreferences(customer, null);
            saveCustomerSelection(customer);
            if (window.location.pathname.startsWith('/cart/review')) {
                showCart();
            }
        };

        cartCount > 0 ? clearCartAndSetCustomer() : loadPreferencesAndShowCart();
    };

    const saveCustomerSelection = async (customer) => {
        try {
            const response = await UsersService.updateSelectedCustomer(customer.id);
            const saved = await response.json();
            saved ? console.info('Customer selection saved.') : console.error('Failed to save customer selection.');
        } catch (error) {
            console.error('Error updating customer selection:', error);
        }
    };

    const setCustomer = (customer, customerUpdated) => {
        loadCustomerPreferences(customer, customerUpdated);
        CartService.resetItemsAvailability();
        cartActions.ClearAllFetching();
        return customer;
    };

    const loadCustomerPreferences = (customer, customerUpdated) => {
        if (customer && customer?.companyCode && customer?.companyCode.length > 0) {
            CustomerService.lookupCustomerPreferences(customer).then((prefs) => {
                let cust = setFreightHandlingCodeByCompanyCode(prefs, customer);
                setCurrentCustomer(cust);
                userActions.receivedCustomerPreferences({
                    paymentInstrument: cust.paymentInstrument,
                    paymentTermId: cust.paymentTermId,
                    freightHandlingCode: cust.freightHandlingCode,
                });
                const fHC =
                    prefs.freightHandlingCode === ''
                        ? (storefrontConfig.settingsList.DefaultFreightHandlingCode ?? defaultFHC)
                        : prefs.freightHandlingCode;
                const paymentTermId = cust?.parentExternalId != null ? (cust?.paymentTerms ?? prefs?.paymentTermsAR) : prefs?.paymentTermsAR;

                cartActions.UpdateOrderHeader(fHC, null, {
                    paymentTermId,
                    paymentInstrument: prefs.paymentInstrument,
                });
                if (customerUpdated) {
                    customerUpdated(cust);
                }
            });
        }
    };

    const setFreightHandlingCodeByCompanyCode = (prefs, curCustomer) => {
        currentUser.freightHandlingCode =
            storefrontConfig.settingsList.DefaultFreightHandlingCode ?? defaultFHC;

        return {
            ...curCustomer,
            freightHandlingCode:
                prefs.freightHandlingCode === ''
                    ? (storefrontConfig.settingsList.DefaultFreightHandlingCode ?? defaultFHC)
                    : prefs.freightHandlingCode,
            paymentInstrument: prefs.paymentInstrument,
            paymentTermId: prefs.paymentTermsAR,
            customerPORequired: prefs.customerPORequired,
        };
    };

    const userHasRole = (roles) => {
        const rolesList = currentUser.roles;
        return roles.some(function (r) {
            return rolesList.some(function (element) {
                return r.toLowerCase() === element.toLowerCase();
            });
        });
    };

    const handleBetaToggle = () => {
        let method;
        currentUser.userFlags.includes('Beta') ? (method = 'DELETE') : (method = 'POST');
        Api.fetch('/api/user/flags?flagName=Beta', { method: method }).then(setFlags);
    };

    const handlePriceBypassToggle = () => {
        let method;
        currentUser.userFlags.includes('PriceBypass') ? (method = 'DELETE') : (method = 'POST');
        Api.fetch('/api/user/flags?flagName=PriceBypass', { method: method }).then(setFlags);
    };

    const setFlags = (res) => {
        if (res) {
            userActions.SetUserFlags(res);
        }
    };

    const showSearch = () => {
        setSearchIsActive(true);
    };
    const hideSearch = () => {
        setSearchIsActive(false);
    };

    const showCart = () => {
        if (currentUser.storefront === '6400') {
            if (currentCustomer && !CustomerService.checkCreditHold(currentCustomer)) history.push('/cart');
        } else {
            history.push('/cart');
        }
    };

    const showHelp = () => {
        history.push('/help');
    };

    const cartIsDisabled = () => {
        const billTo = currentCustomer && currentCustomer.billingType === '1';
        const userRoleNotAllowed = UsersService.current.role === 'Standard_9No_Ordering0';
        return billTo || userRoleNotAllowed;
    };

    const toggleUserMenu = () => {
        setUserMenuIsActive(!userMenuIsActive);
    };

    const logout = async () => {
        try {
            if (isSaveFiltersEnabled) {
                const response = await UsersService.saveFiltersAsync(invoice, orders, quotes);
                if (!response?.ok) {
                    Notifications.error(localized?.FailedToSaveFilters || "Failed to save filters");
                }
            }
        } catch (err) {
            console.error("Error saving filters on logout:", err);
            Notifications.error(localized?.ErrorSavingFiltersOnLogout || "Error saving filters on logout");
        } finally {
            Auth.signOut();
        }
    };


    const changePasswordClick = () => {
        Auth.changePassword(currentUser.locale, '/change-password-successful');
    };

    const handleUserFavorite = () => {
        history.push({ pathname: '/userfavorite', user: { currentUser } });
    };

    return (
        <div className="fele-user-bar">
            <Link to="/">
                <div className="logo"></div>
            </Link>
            <div className='search-wrapper' >
                <Search selectedCustomer={currentCustomer} isMobile={isMobile} />
            </div>

            <div className="bar-right">

                <div className="shipto">
                    <CustomerSelector
                        currentUser={currentUser}
                        customers={customerList}
                        selectedCustomer={currentCustomer}
                        updateSelectedCustomer={updateSelectedCustomer}
                    />
                </div>

                <div className="cart">
                    <div className="button-wrapper">
                        <button className="cart-trigger" onClick={showCart} disabled={cartIsDisabled()}>
                            <CartIcon />
                        </button>
                    </div>
                </div>

                <div className={`user ${userHasRole(['BetaUser']) ? 'is-beta-user' : ''}`}>
                    <div className="button-wrapper">
                        <button className="user-menu-toggle" onClick={toggleUserMenu} ref={(node) => { nodeToggle.current = node; }}>
                            <UserIcon />
                        </button>
                    </div>

                    <div id="userMenu" className={`menu ${userMenuIsActive ? 'active' : ''}`} ref={(node) => { curNode.current = node; }}>
                        <div className="item d-flex align-items-center gap-4">
                            <span className="fs-1 text-start">
                                {currentUser && currentUser.firstName && currentUser.lastName ? `${currentUser.firstName} ${currentUser.lastName}` : 'N/A'}
                            </span>
                            <EditName isAdmin={false} userActions={userActions} user={currentUser} localized={localized} />
                        </div>

                        <Authorize hasRoles={[Roles.BETA_USER]} displayMessage={false}>
                            <div className="item" hidden>
                                <div className="button-wrapper">
                                    <CheckBox
                                        className="beta-toggle"
                                        onClick={handleBetaToggle}
                                        label={localized.EnableBetaFeatures}
                                        checked={currentUser.userFlags.includes('Beta')}
                                    />
                                </div>
                            </div>
                        </Authorize>

                        <Authorize hasRoles={[Roles.DEVELOPER]} displayMessage={false} style={{ display: 'none' }}>
                            <div className="item" hidden>
                                <div className="button-wrapper">
                                    <CheckBox
                                        className="price-bypass-toggle"
                                        onClick={handlePriceBypassToggle}
                                        label={localized.EnablePriceBypassFeature}
                                        checked={currentUser.userFlags.includes('PriceBypass')}
                                    />
                                </div>
                            </div>
                        </Authorize>

                        {storefrontConfig.settingsList.UseSaveSearchFilters === '1' && (
                            <div className="item">
                                <div className="button-wrapper">
                                    <SaveFiltersToggle localized={localized} setUserFlags={userActions.SetUserFlags} />
                                </div>
                            </div>
                        )}

                        <LanguageSelect
                            user={store.user}
                            localized={localized}
                            changeLanguage={setLocale}
                            language={props.StorefrontConfig.locale}
                        />

                        <div className="button-wrapper">
                            <button className="item log-out" onClick={handleUserFavorite}>
                                {localized.FavoriteItems}
                            </button>
                        </div>
                        <div className="button-wrapper">
                            <button className="item log-out" onClick={showHelp}>
                                {localized.Help}
                            </button>
                        </div>
                        <div className="button-wrapper">
                            <button className="item log-out" onClick={changePasswordClick}>{localized.ChangePassword}</button>
                        </div>

                        <div className="button-wrapper">
                            <button className="item log-out" onClick={logout}>{localized.LogOut}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

UserBar.propTypes = {
    currentUser: PropTypes.object,
    cartCount: PropTypes.number,
    availabilityByLocation: PropTypes.bool,
    hideYourCost: PropTypes.bool,
};

function mapStateToProps(state) {
    return {
        currentUser: state.user.currentUser,
        cartCount: state.cart.items.length,
    };
}

export default compose(
    connect(mapStateToProps, null)
)(withStorefrontConfig(UserBar));
